import { FunctionComponent, useState, useRef, useEffect } from "react";
import Column from "../Column/Column";
import IconOptionBox, { IconOptionBoxProps } from "./IconOptionBox";
import Button from "../Button/Button";

export interface IconOptionBoxesData {
  id: string;
  title: string;
  heading: string;
  description: string;
  encrypt?: boolean;
  addToScore?: boolean;
  type: string;
  validations: {
    required?: boolean;
  };
  components: Array<IconOptionBoxProps>;
  properties: {
    allowMultipleSelection: boolean;
    allowOtherChoice: boolean;
    randomize: boolean;
    verticalAlignment: boolean;
  };
}

type IconOptionBoxesProps = {
  data: IconOptionBoxesData;
  onDataSelected: Function;
  onBack?: Function;
  errors: { [key: string]: String };
  isActive: boolean;
  nextPage?: string;
  isLoading?: boolean;
  answers: any;
};

interface SelectProps {
  id: string;
  label: string;
  value?: number;
}

const IconOptionBoxes: FunctionComponent<IconOptionBoxesProps> = ({
  data,
  onDataSelected,
  onBack,
  isActive,
  errors,
  nextPage,
  isLoading,
  answers,
}) => {
  const [choices, setChoices] = useState<Array<SelectProps>>([]);
  const [choicesArray, setChoicesArray] = useState<Array<string>>([]);

  const ref = useRef(choices);

  useEffect(() => {
    ref.current = choices;
  }, [choices]);

  useEffect(() => {
    if (isActive) {
      document.addEventListener("keydown", onContinue);
      return () => {
        document.removeEventListener("keydown", onContinue);
      };
    }
    return;
  }, [isActive]);

  const onContinue = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.keyCode === 13) {
      onSelectData();
    }
  };

  const onBoxSelected = ({
    id,
    label,
    value,
    disabled,
  }: SelectProps & { disabled: boolean }) => {
    const index = choices.findIndex((el) => el.id === id);
    if (disabled) return;
    let selectedData = null;
    if (index === -1 && data.properties.allowMultipleSelection) {
      selectedData = { id, label, value };
      setChoices((current) => [...current, { id, label, value }]);
      setChoicesArray((current) => [...current, id]);
    } else if (index === -1) {
      selectedData = { id, label, value };
      setChoices((current) => [{ id, label, value }]);
      setChoicesArray((current) => [id]);
    } else {
      setChoices((current) => [
        ...current.slice(0, index),
        ...current.slice(index + 1),
      ]);
      setChoicesArray((current) => [
        ...current.slice(0, index),
        ...current.slice(index + 1),
      ]);
    }
    // } else {
    //   setChoices([{ id, label, value }]);
    //   setChoicesArray([id]);
    //   onSelectData({ id, label, value });
    // }
    if (!data.properties.allowMultipleSelection && selectedData) {
      onSelectData({ id, label, value });
    }
  };

  const content: JSX.Element[] = [];
  data.components.forEach(
    (el) =>
      (content[el.index] = (
        <div
          className={`p-2 w-full lg:p-4 ${
            data.components.length === 2 ? "lg:w-1/2" : "lg:w-1/3"
          }`}
          key={el.id}
        >
          <IconOptionBox
            {...el}
            answers={answers}
            onSelect={(calculatedValue?: number) =>
              onBoxSelected({
                id: el.id,
                label: el.heading,
                value: calculatedValue ?? el.value,
                disabled: el.disabled,
              })
            }
            questionId={data.id}
            isSelected={choicesArray.includes(el.id)}
          />
        </div>
      ))
  );

  const onSelectData = (customAnswer?: { [key: string]: any }) => {
    let answer: { [key: string]: any } = {};

    if (customAnswer) {
      answer[customAnswer.id] = customAnswer;
    } else {
      ref.current.forEach((el) => {
        answer[el.id] = el;
      });
    }
    const selectedData = {
      type: "multiple_choice",
      page: { type: "icon_multiple_choice", id: data.id },
      data: answer,
    };
    onDataSelected(selectedData);
  };

  return (
    <div>
      <div className="flex flex-wrap items-stretch mb-12 lg:-m-4 lg:mb-12">
        {content}
      </div>
      {errors?.general ? (
        <p className="text-center mb-4 -mt-6 text-red-500">{errors.general}</p>
      ) : null}
      <div className="flex flex-wrap justify-center mb-12 -mx-2 lg:-mx-4">
        {onBack ? (
          <div className="px-2 lg:py-0 lg:px-4 lg:w-1/4">
            <Button variant="outline" onPress={() => onBack()}>
              Zurück
            </Button>
          </div>
        ) : null}
        {data.properties.allowMultipleSelection ||
        !data.validations?.required ? (
          <div className="px-2 lg:py-0 lg:px-4 lg:w-1/4">
            <Button
              variant="primary"
              onPress={() => onSelectData()}
              isLoading={isLoading}
            >
              {nextPage ? "Weiter" : "Anmelden"}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default IconOptionBoxes;
